import { Heart, X, Flame, BookOpen, Library, Feather } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  {
    amount: 3,
    once: "askgod_donate_3",
    monthly: "askgod_monthly_3",
    label: "Light a Candle",
    icon: Flame,
    testid: "donate-preset-3",
  },
  {
    amount: 5,
    once: "askgod_donate_5",
    monthly: "askgod_monthly_5",
    label: "Preserve the Scrolls",
    icon: BookOpen,
    testid: "donate-preset-5",
  },
  {
    amount: 10,
    once: "askgod_donate_10",
    monthly: "askgod_monthly_10",
    label: "Sacred Library",
    icon: Library,
    testid: "donate-preset-10",
  },
];

const MIN = 1;
const MAX = 500;

export const DonateModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [customError, setCustomError] = useState("");
  const [recurring, setRecurring] = useState(false);

  if (!open) return null;

  const openCheckout = async (body, loadingKey) => {
    if (loading) return;
    setLoading(loadingKey);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        ...body,
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        // Break out of iframe (if we're in one) so Stripe can load — Stripe blocks embedding.
        try {
          const opened = window.open(res.data.checkout_url, "_top");
          if (!opened) throw new Error("blocked");
        } catch {
          window.location.href = res.data.checkout_url;
        }
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the checkout. Please try again.");
      setLoading(null);
    }
  };

  const handlePreset = (preset) => {
    const lookup = recurring ? preset.monthly : preset.once;
    openCheckout({ lookup_key: lookup }, lookup);
  };

  const handleCustom = (e) => {
    e?.preventDefault();
    const num = parseFloat(customAmount);
    if (!Number.isFinite(num)) {
      setCustomError("Enter a number.");
      return;
    }
    if (num < MIN || num > MAX) {
      setCustomError(`Any amount from $${MIN} to $${MAX}.`);
      return;
    }
    setCustomError("");
    const rounded = Math.round(num * 100) / 100;
    openCheckout({ amount: rounded, recurring }, "custom");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#FFFFFF]/40 backdrop-blur-sm fade-up"
      onClick={onClose}
      data-testid="donate-modal-backdrop"
    >
      <div
        data-testid="donate-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-2xl bg-[#5C0E1D] gold-border-strong p-7 sm:p-9"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="donate-close-btn"
          className="absolute top-4 right-4 text-[#FBEBD3] hover:text-[#E7B04A] p-1.5 rounded-full hover:bg-[#E7B04A]/10"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 text-[#E7B04A] mb-2">
          <Heart className="w-4 h-4" strokeWidth={1.6} />
          <span className="text-xs sm:text-sm uppercase tracking-[0.16em] font-sans-clean font-bold">
            Keep the Candle Lit
          </span>
        </div>
        <h3 className="font-serif-display text-3xl text-[#FFFFFF] leading-tight">
          Ask God is <em className="text-[#E7B04A]">free, forever</em>.
        </h3>
        <p className="mt-3 text-sm text-[#FBEBD3] leading-relaxed">
          If these words have offered you a moment of light, consider a small gift.
          Payments are handled securely by Stripe — no account required.
        </p>

        {/* One-time / Monthly toggle */}
        <div
          data-testid="donate-frequency-toggle"
          className="mt-5 flex w-full sm:w-auto sm:inline-flex p-1 rounded-full bg-[#4A0A17] gold-border"
        >
          <button
            type="button"
            onClick={() => setRecurring(false)}
            disabled={!!loading}
            data-testid="donate-frequency-once"
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-1.5 rounded-full text-[13px] sm:text-sm uppercase tracking-[0.1em] sm:tracking-[0.16em] font-sans-clean font-bold transition-all whitespace-nowrap ${
              !recurring
                ? "bg-[#FFFFFF] text-[#5C0E1D]"
                : "text-[#FBEBD3] hover:text-[#E7B04A]"
            }`}
          >
            One-time
          </button>
          <button
            type="button"
            onClick={() => setRecurring(true)}
            disabled={!!loading}
            data-testid="donate-frequency-monthly"
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-1.5 rounded-full text-[13px] sm:text-sm uppercase tracking-[0.1em] sm:tracking-[0.16em] font-sans-clean font-bold transition-all whitespace-nowrap ${
              recurring
                ? "bg-[#FFFFFF] text-[#5C0E1D]"
                : "text-[#FBEBD3] hover:text-[#E7B04A]"
            }`}
          >
            Monthly
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PRESETS.map((p) => {
            const Icon = p.icon;
            const activeLookup = recurring ? p.monthly : p.once;
            const isLoading = loading === activeLookup;
            return (
              <button
                key={p.testid}
                data-testid={p.testid}
                onClick={() => handlePreset(p)}
                disabled={!!loading}
                className="group rounded-xl border border-[#E7B04A]/40 bg-[#4A0A17]/50 p-4 text-left hover:border-[#E7B04A] hover:bg-[#4A0A17] transition-all disabled:opacity-60 disabled:cursor-wait"
              >
                <Icon className="w-5 h-5 text-[#E7B04A] mb-2" strokeWidth={1.5} />
                <div className="font-serif-display text-2xl text-[#FFFFFF] leading-none flex items-baseline gap-1">
                  ${p.amount}
                  {recurring && (
                    <span className="text-xs text-[#FBEBD3] font-sans-clean uppercase tracking-[0.1em]">
                      /mo
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm sm:text-sm uppercase tracking-[0.16em] text-[#FBEBD3] font-sans-clean">
                  {isLoading ? "Opening…" : p.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* Custom amount */}
        <div className="mt-5 pt-5 border-t border-[#E7B04A]/25">
          <div className="flex items-center gap-2 text-[#E7B04A] mb-2">
            <Feather className="w-3.5 h-3.5" strokeWidth={1.6} />
            <span className="text-xs sm:text-[0.75rem] uppercase tracking-[0.16em] font-sans-clean font-bold">
              Or give from the heart {recurring ? "each month" : ""}
            </span>
          </div>
          <form onSubmit={handleCustom} noValidate className="flex items-stretch gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-serif-display text-[#E7B04A] text-lg">
                $
              </span>
              <input
                data-testid="donate-custom-input"
                type="text"
                inputMode="decimal"
                placeholder={recurring ? "Any amount per month" : "Any amount"}
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  if (customError) setCustomError("");
                }}
                disabled={!!loading}
                className="w-full rounded-xl bg-[#4A0A17]/50 gold-border pl-8 pr-16 py-3 text-lg font-scripture text-[#FFFFFF] placeholder:text-[#FBEBD3]/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-[#E7B04A]/40 disabled:opacity-60"
              />
              {recurring && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm sm:text-sm uppercase tracking-[0.14em] text-[#FBEBD3] font-sans-clean">
                  /mo
                </span>
              )}
            </div>
            <button
              type="submit"
              data-testid="donate-custom-submit"
              disabled={!!loading || !customAmount}
              className="inline-flex items-center gap-2 rounded-xl bg-[#E7B04A] px-5 text-sm text-[#5C0E1D] hover:bg-[#F5C76B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-sans-clean uppercase tracking-[0.16em] text-sm sm:text-sm"
            >
              {loading === "custom" ? "Opening…" : "Give"}
            </button>
          </form>
          {customError && (
            <p
              data-testid="donate-custom-error"
              className="mt-2 text-xs text-[#E7B04A] italic font-scripture"
            >
              {customError}
            </p>
          )}
          <p className="mt-2 text-sm sm:text-sm text-[#FBEBD3] font-scripture italic">
            Any amount from ${MIN} to ${MAX}
            {recurring ? " per month. Cancel anytime from your receipt email." : ". Every candle counts."}
          </p>
        </div>

        <p className="mt-6 text-sm sm:text-sm text-[#FBEBD3] italic text-center font-scripture">
          &ldquo;Freely you have received; freely give.&rdquo; — Matthew 10:8
        </p>
      </div>
    </div>
  );
};

export default DonateModal;
